import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { useUpdatePlannedOrder, type PlannedOrder } from "@/api/planningRun";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

/** The server sizes the order so the stock lasts until the date, and firms it. */
export function LastsUntilDialog({
  order,
  onClose,
}: {
  order: PlannedOrder;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdatePlannedOrder();
  const form = useForm({
    resolver: zodResolver(z.object({ lastsUntil: z.string().min(1) })),
    defaultValues: { lastsUntil: "" },
  });
  const submit = form.handleSubmit(({ lastsUntil }) =>
    update.mutate(
      { run: order.run, id: order.id, status: "firmed", lastsUntil },
      { onSuccess: onClose },
    ),
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("planningReview.sizeToLast", "Size to last until")}
          </DialogTitle>
          <DialogDescription>
            {order.itemSku} · {order.itemName} · {order.locationName}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form id="lasts-until" onSubmit={submit}>
            <FormField
              control={form.control}
              name="lastsUntil"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("planningReview.lastsUntil", "Lasts until")}
                  </FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
        {update.isError && (
          <p role="alert" className="text-destructive text-sm">
            {t(
              "planningReview.saveFailed",
              "Could not save this order. Reload the plan and try again.",
            )}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("common.buttons.cancel", "Cancel")}
          </Button>
          <Button type="submit" form="lasts-until" disabled={update.isPending}>
            {t("planningReview.saveAndAccept", "Save and accept")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
